import { randomUUID } from 'crypto';
import type { IncomingHttpHeaders } from 'http';

import { EventbriteHeader } from '@eventbrite/webapp-constants';

import { X_EB_LISTINGS_DEBUG } from '@/lib/cookies/constants';

/**
 * Reads a single header value from Node request `headers`, handling array values and whitespace.
 */
export function getHeaderValue(headers: IncomingHttpHeaders, name: string): string | undefined {
  const raw = headers[name];
  const value = Array.isArray(raw) ? raw[0] : raw;
  const trimmed = value?.trim();
  return trimmed || undefined;
}

/**
 * Reads Eventbrite Lambda-authorizer headers from Node request `headers`.
 * Shared by Next.js API routes and `getServerSideProps` (same header names).
 */
export function getSoaTokenFromHeaders(headers: IncomingHttpHeaders): string | undefined {
  return getHeaderValue(headers, EventbriteHeader.EB_SOA_TOKEN);
}

export function getUserIdFromHeaders(headers: IncomingHttpHeaders): string | undefined {
  return getHeaderValue(headers, EventbriteHeader.EB_USER_ID);
}

export function getSessionIdFromHeaders(headers: IncomingHttpHeaders): string | undefined {
  return getHeaderValue(headers, EventbriteHeader.EB_SESSION_ID);
}

export function getGuestIdFromHeaders(headers: IncomingHttpHeaders): string | undefined {
  return getHeaderValue(headers, EventbriteHeader.EB_GUEST_ID);
}

/**
 * Whether the request carries the trusted edge-issued listings debug signal.
 * The proxy only re-stamps the header for an exact `true` on a listings surface
 * path, so any other value means "not a debug request".
 */
export function isListingsDebugRequestedFromHeaders(headers: IncomingHttpHeaders): boolean {
  return getHeaderValue(headers, X_EB_LISTINGS_DEBUG) === 'true';
}

export function getCorrelationIdFromHeaders(
  headers: IncomingHttpHeaders,
  fallback: () => string = () => randomUUID(),
): string {
  return getHeaderValue(headers, EventbriteHeader.EB_CORRELATION_ID) ?? fallback();
}
